import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

interface Event {
  id: string;
  title: string;
  category: string;
  start_date: string;
  location_city: string | null;
  location_country: string | null;
  location_type: string;
  cover_image: string | null;
  price: number | null;
  currency: string | null;
}

function getWeekendRange() {
  const now = new Date();
  const day = now.getDay();
  const daysToSat = (6 - day + 7) % 7 || 7;
  const sat = new Date(now);
  sat.setDate(now.getDate() + (day === 0 ? 0 : daysToSat));
  sat.setHours(0, 0, 0, 0);
  const sun = new Date(sat);
  sun.setDate(sat.getDate() + (day === 0 ? 0 : 1));
  sun.setHours(23, 59, 59, 999);
  return { start: sat, end: sun };
}

export default function ThisWeekendPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [registeredIds, setRegisteredIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const { start, end } = getWeekendRange();

  const firstName = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || null;

  useEffect(() => {
    document.title = 'Events This Weekend | EVENTSHOWCASE';
    fetchWeekendEvents();

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Events This Weekend',
      description: `Events happening this weekend: ${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
      url: `${SITE_URL}/events/this-weekend`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'EVENTSHOWCASE',
        url: SITE_URL,
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: events.map((event, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Event',
            name: event.title,
            startDate: event.start_date,
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: event.location_type === 'online' 
              ? 'https://schema.org/OnlineEventAttendanceMode' 
              : 'https://schema.org/OfflineEventAttendanceMode',
            location: event.location_type === 'online'
              ? { '@type': 'VirtualLocation', url: `${SITE_URL}/event/${event.id}` }
              : { '@type': 'Place', name: [event.location_city, event.location_country].filter(Boolean).join(', ') || 'TBA' },
            offers: {
              '@type': 'Offer',
              price: event.price || 0,
              priceCurrency: event.currency || 'USD',
              availability: 'https://schema.org/InStock',
            },
          },
        })),
      },
    };

    const existing = document.getElementById('schema-this-weekend');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-this-weekend';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-this-weekend');
      if (el) el.remove();
    };
  }, [events]);

  useEffect(() => {
    if (user) fetchRegisteredIds();
  }, [user]);

  const fetchWeekendEvents = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('events')
      .select('id, title, category, start_date, location_city, location_country, location_type, cover_image, price, currency')
      .eq('is_published', true)
      .gte('start_date', start.toISOString())
      .lte('start_date', end.toISOString())
      .order('start_date', { ascending: true })
      .limit(30);
    setEvents(data || []);
    setLoading(false);
  };

  const fetchRegisteredIds = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('registrations')
      .select('event_id')
      .eq('user_id', user.id);
    if (data) setRegisteredIds(new Set(data.map((r: any) => r.event_id)));
  };

  const weekendLabel = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) +
    ' – ' + end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const myWeekendEvents = events.filter(e => registeredIds.has(e.id));

  return (
    <div className="min-h-screen bg-white">
      <Navbar forceScrolled />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold uppercase tracking-wide mb-5">
            <i className="ri-sun-line"></i> Weekend Picks
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
            {firstName ? `${firstName}'s Weekend` : 'This Weekend'}
          </h1>
          <p className="text-base text-gray-500 mb-2">{weekendLabel}</p>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            {firstName
              ? `Don't let the weekend slip by, ${firstName}. Here's what's happening.`
              : "Don't let the weekend slip by. Here's what's happening right now."}
          </p>

          {/* Personalised weekend summary for logged-in users */}
          {user && myWeekendEvents.length > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white border border-orange-200 rounded-full px-4 py-2 text-sm text-orange-700">
              <i className="ri-calendar-check-line"></i>
              <span>You&apos;re already going to <strong>{myWeekendEvents.length}</strong> event{myWeekendEvents.length > 1 ? 's' : ''} this weekend!</span>
            </div>
          )}
          {user && myWeekendEvents.length === 0 && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white border border-orange-200 rounded-full px-4 py-2 text-sm text-orange-700">
              <i className="ri-calendar-line"></i>
              <span>Your weekend is free — find something to do below</span>
            </div>
          )}
        </div>
      </section>

      {/* My weekend events (if any) */}
      {user && myWeekendEvents.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pt-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-smile-line text-orange-600"></i>
            </div>
            <h2 className="text-base font-bold text-gray-900">Your plans this weekend</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {myWeekendEvents.map(event => (
              <div
                key={event.id}
                onClick={() => navigate(`/event/${event.id}`)}
                className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-100 rounded-xl cursor-pointer hover:border-orange-300 transition-all"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-orange-100">
                  {event.cover_image
                    ? <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full flex items-center justify-center"><i className="ri-calendar-event-line text-orange-400 text-xl"></i></div>
                  }
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{event.title}</p>
                  <p className="text-xs text-orange-600 mt-0.5">
                    {new Date(event.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <span className="text-[10px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded-full">Registered</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 mb-8"></div>
        </section>
      )}

      {/* All events */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-line text-3xl text-orange-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nothing scheduled this weekend yet</h3>
            <p className="text-gray-500 text-sm mb-5">Check back soon or browse all upcoming events</p>
            <Link to="/discover" className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">
              Browse All Events
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-6">{events.length} event{events.length !== 1 ? 's' : ''} this weekend</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(event => {
                const isRegistered = registeredIds.has(event.id);
                return (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/event/${event.id}`)}
                    className={`bg-white border rounded-2xl overflow-hidden transition-all cursor-pointer group ${
                      isRegistered ? 'border-green-200' : 'border-gray-100 hover:border-orange-200'
                    }`}
                  >
                    <div className="relative h-48 bg-gray-100 overflow-hidden">
                      {event.cover_image ? (
                        <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-orange-50">
                          <i className="ri-calendar-event-line text-4xl text-orange-300"></i>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <span className="text-white text-xs font-semibold">
                          {new Date(event.start_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      {isRegistered && (
                        <span className="absolute top-3 right-3 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-check-line text-[10px]"></i> Going
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <span className="text-[10px] font-bold text-orange-600 uppercase tracking-wide">{event.category}</span>
                      <h3 className="font-semibold text-sm text-gray-900 mt-1 mb-2 line-clamp-2">{event.title}</h3>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <i className={event.location_type === 'online' ? 'ri-global-line' : 'ri-map-pin-line'}></i>
                          <span className="truncate max-w-[120px]">
                            {event.location_type === 'online' ? 'Online' : [event.location_city, event.location_country].filter(Boolean).join(', ') || 'TBA'}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-teal-600">
                          {!event.price || event.price === 0 ? 'Free' : `${event.currency || '$'}${event.price}`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {!user && (
        <section className="bg-orange-50 border-t border-orange-100 py-10 px-4 text-center">
          <p className="text-gray-700 font-medium mb-3">Log in to see which events you&apos;re already attending this weekend</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/login" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap">
              Log In
            </Link>
            <Link to="/signup" className="inline-flex items-center gap-2 border border-gray-200 text-gray-700 hover:bg-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap">
              Sign Up Free
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
